import {
  ALLOWLISTABLE_GUEST_FIELDS,
  ALLOWLIST_ADDITIONAL_PREFIX,
  ALLOWLIST_KEY_IMPLIES,
  isImpliedAllowlistToken,
  normalizeAllowlist,
} from "@/lib/authz/fieldAllowlist";
import { SMS_GUEST_FIELD_NAMES } from "@/lib/sms/smsCapabilities";

/**
 * B2 "All fields except …" for role templates (plan Rev. 5.2, FE-1).
 *
 * The explicit `fieldAllowlist` stays the ONLY authz truth. "All except" is
 * editor metadata on top of it (`fieldAllowlistMode: "exclude"` +
 * `fieldExclusions`): the list holds what the role may read, the exclusions
 * what the admin decided to hide, and everything else in the catalogue is
 * "new — not released". New fields therefore stay hidden until somebody
 * releases them; nothing here ever derives the list from "catalogue minus
 * exclusions".
 *
 * Compile = an EDIT of the stored list: every action changes only the keys it
 * names (they join `touched`); an untouched key keeps its stored state —
 * including keys outside today's catalogue, custom keys whose field is gone
 * and, in "All except", stray projection tokens (`keepStrayCanonical`).
 *
 * Pure: no React, no i18n, no I/O. Every lookup keyed by a stored string goes
 * through a Map/Set or `hasOwnProperty` — a stored key may be `constructor`.
 */

export type AllowlistMode = "include" | "exclude";

export type FieldSensitivity = "NORMAL" | "SENSITIVE" | "ART9";

/** The session memory "All fields" keeps of the state it replaced. */
export interface FieldScopeMemory {
  list: string[];
  mode: AllowlistMode;
  exclusions: string[];
  exclusionsKnown: boolean;
}

export interface FieldScopeState {
  /** The explicit allowlist (authz truth); null = all fields. */
  list: string[] | null;
  /** Ignored while `list === null`. */
  mode: AllowlistMode;
  /** Keys the admin decided to hide. Only sent while `mode === "exclude"`. */
  exclusions: string[];
  /** Keys the admin changed in this session. */
  touched: ReadonlySet<string>;
  /**
   * Whether `exclusions` is a real decision (a stored "All except" template,
   * or one made in this session). While false, the first switch to "All
   * except" derives it from the catalogue. Absent = false.
   */
  exclusionsKnown?: boolean;
  /** What ticking "All fields" replaced; unticking restores it. */
  beforeAllFields?: FieldScopeMemory | null;
}

export interface CatalogueEntry {
  /** Allowlist key: a bare built-in name or `additionalFields.<name>`. */
  key: string;
  /** null = a level this bundle does not know: treated as sensitive. */
  sensitivity: FieldSensitivity | null;
  custom: boolean;
  /** The custom field's display label (translation, else its name). */
  label?: string;
}

/** The persisted shape the scope is seeded from (template GET / response). */
export interface FieldScopeSource {
  fieldAllowlist?: string[] | null;
  fieldAllowlistMode?: string | null;
  fieldExclusions?: string[] | null;
}

export interface FieldScopePayload {
  fieldAllowlist: string[] | null;
  fieldAllowlistMode?: AllowlistMode;
  fieldExclusions?: string[];
}

export type FieldScopeAction =
  | { type: "setAllFields"; on: boolean }
  | { type: "setMode"; mode: AllowlistMode; catalogue: readonly CatalogueEntry[] }
  /** The caller ran the sensitive confirm before dispatching. */
  | { type: "show"; keys: string[] }
  /** Also hides the keys' revealers (REVEALS) in "All except", marking them touched. */
  | { type: "hide"; keys: string[] }
  /** View import "Replace": switches to "Only these". */
  | { type: "replaceList"; list: string[] }
  /** View import "Add" / Aktion grants: stays in its mode; a no-op on "All fields". */
  | { type: "addList"; keys: string[] };

export type VisibleMarker = "deleted" | "outsideCatalogue" | "stray" | "revealsLastName";
export type HiddenTag = "revealsLastName" | "sensitive" | "new";

export interface VisibleRow {
  key: string;
  marker?: VisibleMarker;
  /** With "revealsLastName": the hidden keys this visible key reveals. */
  reveals?: string[];
}

export interface HiddenRow {
  key: string;
  /** Absent = hidden by the admin's own decision (a normal field). */
  tag?: HiddenTag;
  /** With "revealsLastName": the keys showing this one would reveal. */
  reveals?: string[];
}

export interface EditorGroups {
  visible: VisibleRow[];
  hidden: HiddenRow[];
  /** Keys the list does not hold but reveals ("visible via Companions"). */
  revealedVia: { key: string; via: string[] }[];
  /** "Show all new (normal fields)" candidates. */
  newNormal: string[];
  /** "N of M visible" over the catalogue; null while it is unknown. */
  counts: { visible: number; total: number } | null;
}

/** The backend's `@Size(max = 200)` on `fieldAllowlist`. */
export const FIELD_ALLOWLIST_CAP = 200;

/**
 * Access data: a role holding one of these can sign in as the guest or read
 * the credential. Hidden by default in "All except" and tagged like a
 * sensitive field.
 */
export const ACCESS_DATA_KEYS: readonly string[] = ["code", "username", "loginToken"];
const ACCESS_DATA_SET: ReadonlySet<string> = new Set(ACCESS_DATA_KEYS);

/**
 * Key → the guest keys a read of it also returns (nested lookups project the
 * names; a username is built from them; a login token carries code and
 * username). Flattened/transitive; byte-identical with the backend through
 * `__fixtures__/field-reveals.json`. "All except" only — the masker and
 * "Only these" are unchanged.
 */
export const REVEALS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  companions: Object.freeze(["firstName", "lastName"]),
  mainGuestDTO: Object.freeze(["firstName", "lastName"]),
  username: Object.freeze(["firstName", "lastName"]),
  loginToken: Object.freeze(["code", "username", "firstName", "lastName"]),
});

const NONE: readonly string[] = Object.freeze([]);

const own = (obj: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key);

/** What a read of `key` reveals besides itself (REVEALS row, or none). */
export const revealsOf = (key: string): readonly string[] =>
  own(REVEALS, key) ? REVEALS[key] : NONE;

const impliedOf = (key: string): readonly string[] =>
  own(ALLOWLIST_KEY_IMPLIES, key) ? ALLOWLIST_KEY_IMPLIES[key] : NONE;

/** revealed key → the keys that reveal it. */
const REVEALERS: ReadonlyMap<string, readonly string[]> = (() => {
  const m = new Map<string, string[]>();
  for (const revealer of Object.keys(REVEALS)) {
    for (const k of REVEALS[revealer]) m.set(k, [...(m.get(k) ?? []), revealer]);
  }
  return m;
})();

/** projection token → the columns that carry it. */
const TOKEN_COLUMNS: ReadonlyMap<string, readonly string[]> = (() => {
  const m = new Map<string, string[]>();
  for (const column of Object.keys(ALLOWLIST_KEY_IMPLIES)) {
    for (const token of ALLOWLIST_KEY_IMPLIES[column]) m.set(token, [...(m.get(token) ?? []), column]);
  }
  return m;
})();

/** A custom field's key (`additionalFields.<name>`). */
export const isCustomKey = (key: string): boolean =>
  typeof key === "string" && key.startsWith(ALLOWLIST_ADDITIONAL_PREFIX);

/** The backend's `AdditionalFieldConstraints.KEY_PATTERN`: a legal custom field name. */
const CUSTOM_FIELD_NAME = /^[a-zA-Z0-9_-]{1,100}$/;
/** The backend's generic `EventRoleTemplateService.FIELD_KEY` (built-ins, legacy dotted keys). */
const GENERIC_FIELD_KEY = /^[a-zA-Z0-9._-]{1,100}$/;

/**
 * A key the backend accepts in a template's field list or exclusions
 * (`EventRoleTemplateService.isAllowlistableKey`): `additionalFields.<name>`
 * with a legal field name (so up to 117 characters), or any generic key of
 * up to 100. Anything else answers `invalidAllowlistKey` /
 * `invalidExclusionKey` — B2 F1 C-LOW-5.
 */
export function isAllowlistShapedKey(key: string): boolean {
  if (typeof key !== "string") return false;
  if (isCustomKey(key) && CUSTOM_FIELD_NAME.test(key.slice(ALLOWLIST_ADDITIONAL_PREFIX.length))) return true;
  return GENERIC_FIELD_KEY.test(key);
}

/**
 * The custom field a key belongs to (contract §7): custom field names never
 * contain a dot (`[a-zA-Z0-9_-]{1,100}`), so a legacy dotted key like
 * `additionalFields.photo.url` belongs to its BASE field `photo`
 * (`additionalFields.photo`). Every other key is its own base.
 */
export function customBaseKey(key: string): string {
  if (!isCustomKey(key)) return key;
  const rest = key.slice(ALLOWLIST_ADDITIONAL_PREFIX.length);
  const dot = rest.indexOf(".");
  return dot === -1 ? key : `${ALLOWLIST_ADDITIONAL_PREFIX}${rest.slice(0, dot)}`;
}

/** A legacy dotted custom key (`additionalFields.<base>.<sub>`). */
const isDottedCustomKey = (key: string): boolean => customBaseKey(key) !== key;

const dedupe = (keys: readonly string[]): string[] => [
  ...new Set(keys.filter((k) => typeof k === "string" && k.length > 0)),
];

/** A projection token whose column is in `present` rides with it — no own row. */
const ridesWithColumn = (key: string, present: ReadonlySet<string>): boolean =>
  isImpliedAllowlistToken(key) && (TOKEN_COLUMNS.get(key) ?? NONE).some((c) => present.has(c));

/** REVEALED(list): the union of REVEALS[k] for k in the list. */
export function revealedBy(list: readonly string[]): Set<string> {
  const out = new Set<string>();
  for (const k of list) for (const r of revealsOf(k)) out.add(r);
  return out;
}

/**
 * `normalizeAllowlist`, except that a stray projection token (its column is
 * gone) keeps its place instead of being dropped. "All except" only: there the
 * editor shows a stray token as its own row and keeps it until the admin hides
 * it. Idempotent; null and [] pass through.
 */
export function keepStrayCanonical(list: readonly string[] | null | undefined): string[] | null {
  if (list == null) return null;
  const present = new Set(list);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const key of list) {
    if (seen.has(key) || ridesWithColumn(key, present)) continue;
    seen.add(key);
    out.push(key);
    for (const token of impliedOf(key)) {
      if (seen.has(token)) continue;
      seen.add(token);
      out.push(token);
    }
  }
  return out;
}

/** The wire canonicaliser of a mode: "All except" keeps stray tokens. */
export const canonicalFor = (mode: AllowlistMode, list: readonly string[] | null): string[] | null =>
  mode === "exclude" ? keepStrayCanonical(list) : normalizeAllowlist(list === null ? null : [...list]);

/**
 * The exclusions a save sends: `dedupe(exclusions) − list − implied(list) −
 * REVEALED(list)`, minus every key the backend would refuse as an exclusion
 * (`invalidExclusionKey`, C-LOW-5). Never touches the list.
 *
 * NOT a byte-for-byte mirror of the backend's normalisation (audit r1
 * C-MED-1, contract §1 note): there `implied(list)` is
 * `ProjectionBuilder.IMPLIED_GUEST_FIELDS` (the emailOptIn / smsOptIn twins),
 * here it is `ALLOWLIST_KEY_IMPLIES` (seating → seatInfo, slotting →
 * appointments, companions → mainGuestDTO). Real payloads converge anyway:
 * this editor always sends a column together with its projection tokens
 * (`keepStrayCanonical`), so the tokens are list keys on both sides; and the
 * backend's answer is adopted after every save. A hand-built list
 * `["companions"]` without `mainGuestDTO` keeps an excluded `mainGuestDTO` on
 * the backend (pinned by e2e REX-6f).
 */
export function normaliseExclusions(exclusions: readonly string[], list: readonly string[]): string[] {
  const drop = new Set<string>(list);
  for (const k of list) for (const t of impliedOf(k)) drop.add(t);
  for (const k of revealedBy(list)) drop.add(k);
  return dedupe(exclusions).filter((k) => !drop.has(k) && isAllowlistShapedKey(k));
}

/** Seed the editor from a stored template. Changes nothing: nothing arms Save on load. */
export function initialFieldScope(t: FieldScopeSource): FieldScopeState {
  const list = Array.isArray(t.fieldAllowlist) ? [...t.fieldAllowlist] : null;
  const exclude = list !== null && t.fieldAllowlistMode === "exclude";
  return {
    list,
    mode: exclude ? "exclude" : "include",
    exclusions:
      exclude && Array.isArray(t.fieldExclusions)
        ? t.fieldExclusions.filter((k): k is string => typeof k === "string")
        : [],
    touched: new Set<string>(),
    exclusionsKnown: exclude,
    beforeAllFields: null,
  };
}

const SENSITIVITY_RANK: Record<FieldSensitivity, number> = { NORMAL: 0, SENSITIVE: 1, ART9: 2 };
const rank = (s: FieldSensitivity | null): number => (s === null ? 3 : SENSITIVITY_RANK[s]);

/** Key → entry; on a duplicate key the stricter sensitivity wins. */
function catalogueIndex(catalogue: readonly CatalogueEntry[]): Map<string, CatalogueEntry> {
  const m = new Map<string, CatalogueEntry>();
  for (const e of catalogue) {
    if (!e || typeof e.key !== "string") continue;
    const prev = m.get(e.key);
    if (!prev || rank(e.sensitivity) > rank(prev.sensitivity)) m.set(e.key, e);
  }
  return m;
}

const isRestricted = (s: FieldSensitivity | null | undefined): boolean => s !== "NORMAL";

/** Hidden by default when "All except" starts from an empty list. */
export function defaultExclusions(catalogue: readonly CatalogueEntry[]): string[] {
  const out = new Set<string>(ACCESS_DATA_KEYS);
  for (const e of catalogueIndex(catalogue).values()) if (isRestricted(e.sensitivity)) out.add(e.key);
  return [...out];
}

/**
 * The keys that need the explicit "sensitive" confirm before they become
 * visible: the access data (`code`, `username`, `loginToken` — door
 * credentials; contract §3b.1), SENSITIVE, ART9, an unknown level, and a
 * custom key the catalogue does not know (unknown sensitivity). In input
 * order, deduped.
 */
export function needsSensitiveConfirm(keys: readonly string[], catalogue: readonly CatalogueEntry[]): string[] {
  const idx = catalogueIndex(catalogue);
  return dedupe(keys).filter((k) => {
    if (ACCESS_DATA_SET.has(k)) return true;
    const e = idx.get(k);
    return e ? isRestricted(e.sensitivity) : isCustomKey(k);
  });
}

/** The level a sensitive question is asked at: access data, the catalogue's level, or an unknown one. */
export type SensitiveLevel = "ACCESS" | FieldSensitivity | "UNKNOWN";

/**
 * Each key's level on this catalogue, as `needsSensitiveConfirm` reads it:
 * access data first, then the catalogue's level (the stricter on a duplicate
 * key); an unknown level and a key the catalogue does not know are `UNKNOWN`.
 */
export function sensitiveLevelsOf(
  keys: readonly string[],
  catalogue: readonly CatalogueEntry[],
): Map<string, SensitiveLevel> {
  const idx = catalogueIndex(catalogue);
  return new Map(
    keys.map((k): [string, SensitiveLevel] => [
      k,
      ACCESS_DATA_SET.has(k) ? "ACCESS" : (idx.get(k)?.sensitivity ?? "UNKNOWN"),
    ]),
  );
}

/** Drop the SMS built-ins unless the channel is on — the pane's own predicate. */
export function withoutSmsEntriesUnless(
  catalogue: readonly CatalogueEntry[],
  smsEnabled: boolean,
): CatalogueEntry[] {
  if (smsEnabled) return [...catalogue];
  return catalogue.filter((e) => e.custom || !SMS_GUEST_FIELD_NAMES.has(e.key));
}

const sensitivityOf = (v: unknown): FieldSensitivity | null => {
  if (v == null) return "NORMAL"; // absent = NORMAL (Field.sensitivity)
  return v === "NORMAL" || v === "SENSITIVE" || v === "ART9" ? v : null;
};

/**
 * The guest field catalogue: every built-in allowlistable column (NORMAL) and
 * every GUEST custom definition (`appliesTo` absent/null = GUEST). Unfiltered
 * by the SMS channel — see `withoutSmsEntriesUnless`.
 *
 * A (legacy) custom name outside the backend's KEY_PATTERN is left out: its
 * key can never be granted or excluded (`invalidAllowlistKey` /
 * `invalidExclusionKey`), so offering it — or deriving a default exclusion
 * from it — would only make every "All except" save of the event fail
 * (B2 F1 C-LOW-5).
 */
export function guestCatalogueOf(definitions: readonly unknown[]): CatalogueEntry[] {
  const out: CatalogueEntry[] = ALLOWLISTABLE_GUEST_FIELDS.map((key) => ({
    key,
    sensitivity: "NORMAL" as const,
    custom: false,
  }));
  const custom = new Map<string, CatalogueEntry>();
  for (const def of definitions) {
    if (!def || typeof def !== "object") continue;
    const d = def as { name?: unknown; translation?: unknown; sensitivity?: unknown; appliesTo?: unknown };
    if (d.appliesTo != null && d.appliesTo !== "GUEST") continue;
    if (typeof d.name !== "string" || !CUSTOM_FIELD_NAME.test(d.name)) continue;
    const key = `${ALLOWLIST_ADDITIONAL_PREFIX}${d.name}`;
    const sensitivity = sensitivityOf(d.sensitivity);
    const label = typeof d.translation === "string" && d.translation.trim() ? d.translation : d.name;
    const prev = custom.get(key);
    if (!prev) custom.set(key, { key, sensitivity, custom: true, label });
    else if (rank(sensitivity) > rank(prev.sensitivity)) custom.set(key, { ...prev, sensitivity });
  }
  return [...out, ...custom.values()];
}

/**
 * The revealers among `keys` that "show" would withhold in "All except": a
 * key that reveals another one which would not be readable afterwards — not
 * on the list and not already revealed by the list, or explicitly hidden.
 * Shown together, `lastName` + `companions` pass. Empty outside "All except".
 */
export function withheldByRevealRule(s: FieldScopeState, keys: readonly string[]): string[] {
  return splitShow(s, dedupe(keys)).withheld;
}

function splitShow(s: FieldScopeState, keys: string[]): { accepted: string[]; withheld: string[] } {
  if (s.list === null || s.mode !== "exclude") return { accepted: keys, withheld: [] };
  const before = new Set(s.list);
  const revealedBefore = revealedBy(s.list);
  const cand = new Set(keys);
  const withheld: string[] = [];
  for (;;) {
    const after = new Set([...before, ...cand]);
    const hiddenAfter = new Set(s.exclusions.filter((k) => !cand.has(k)));
    const readable = (k: string) => after.has(k) || (revealedBefore.has(k) && !hiddenAfter.has(k));
    const out = [...cand].filter((r) => !before.has(r) && revealsOf(r).some((k) => !readable(k)));
    if (out.length === 0) break;
    for (const r of out) {
      cand.delete(r);
      withheld.push(r);
    }
  }
  return { accepted: keys.filter((k) => cand.has(k)), withheld: keys.filter((k) => withheld.includes(k)) };
}

const withTouched = (s: FieldScopeState, keys: Iterable<string>): ReadonlySet<string> => {
  const next = new Set(s.touched);
  for (const k of keys) next.add(k);
  return next;
};

function showKeys(s: FieldScopeState, raw: readonly string[]): FieldScopeState {
  if (s.list === null) return s; // "All fields" already shows everything
  const { accepted } = splitShow(s, dedupe(raw));
  const before = new Set(s.list);
  const hiddenBefore = new Set(s.exclusions);
  // Only a key whose state changes is an edit — re-adding a listed key must
  // not even re-canonicalise the list (same state back, nothing arms Save).
  const changed = accepted.filter((k) => !before.has(k) || hiddenBefore.has(k));
  if (changed.length === 0) return s;
  const list = canonicalFor(s.mode, [...s.list, ...changed]) as string[];
  const drop = new Set(changed);
  for (const k of changed) for (const t of impliedOf(k)) drop.add(t);
  const exclusions = s.exclusions.filter((k) => !drop.has(k));
  return { ...s, list, exclusions, touched: withTouched(s, changed) };
}

/** keys ∪ every key that reveals one of them (transitively). */
function withRevealers(keys: readonly string[]): string[] {
  const out = [...keys];
  const seen = new Set(keys);
  for (let i = 0; i < out.length; i++) {
    for (const r of REVEALERS.get(out[i]) ?? NONE) {
      if (seen.has(r)) continue;
      seen.add(r);
      out.push(r);
    }
  }
  return out;
}

function hideKeys(s: FieldScopeState, raw: readonly string[]): FieldScopeState {
  if (s.list === null) return s;
  const keys = dedupe(raw);
  if (keys.length === 0) return s;
  const hidden = s.mode === "exclude" ? withRevealers(keys) : keys;
  const removal = new Set(hidden);
  for (const k of hidden) for (const t of impliedOf(k)) removal.add(t);
  const onList = new Set(s.list);
  const hiddenBefore = new Set(s.exclusions);
  const changed = [...removal].filter(
    (k) => onList.has(k) || (hidden.includes(k) && !isImpliedAllowlistToken(k) && !hiddenBefore.has(k)),
  );
  if (changed.length === 0) return s;
  const list = canonicalFor(
    s.mode,
    s.list.filter((k) => !removal.has(k)),
  ) as string[];
  const exclusions = dedupe([...s.exclusions, ...hidden.filter((k) => !isImpliedAllowlistToken(k))]);
  return { ...s, list, exclusions, touched: withTouched(s, changed) };
}

function replaceList(s: FieldScopeState, raw: readonly string[]): FieldScopeState {
  const list = normalizeAllowlist(dedupe(raw)) as string[];
  const next = new Set(list);
  const prev = s.list ?? [];
  const removed = prev.filter((k) => !next.has(k) && !isImpliedAllowlistToken(k));
  const added = list.filter((k) => !prev.includes(k));
  const exclusions = dedupe([...s.exclusions, ...removed]).filter((k) => !next.has(k));
  return {
    ...s,
    list,
    mode: "include",
    exclusions,
    touched: withTouched(s, [...removed, ...added]),
  };
}

/**
 * A revealer on the list next to an explicitly hidden key it reveals leaves
 * the list (and joins the exclusions) — only used when this module builds a
 * whole list itself (the defaults), never on a loaded list.
 */
function enforceRevealRule(list: string[], exclusions: string[]): { list: string[]; exclusions: string[] } {
  const hidden = new Set(exclusions);
  const broken = list.filter((r) => revealsOf(r).some((k) => hidden.has(k) && !list.includes(k)));
  if (broken.length === 0) return { list, exclusions };
  const removal = new Set(broken);
  for (const r of broken) for (const t of impliedOf(r)) removal.add(t);
  return {
    list: keepStrayCanonical(list.filter((k) => !removal.has(k))) as string[],
    exclusions: dedupe([...exclusions, ...broken.filter((k) => !isImpliedAllowlistToken(k))]),
  };
}

function setMode(s: FieldScopeState, mode: AllowlistMode, catalogue: readonly CatalogueEntry[]): FieldScopeState {
  if (s.mode === mode) return s;
  if (s.list === null || mode === "include") return { ...s, mode };
  // → "All except"
  const keys = [...catalogueIndex(catalogue).keys()];
  if (s.list.length === 0) {
    const defaults = defaultExclusions(catalogue);
    const hidden = new Set(defaults);
    const built = enforceRevealRule(
      keepStrayCanonical(keys.filter((k) => !hidden.has(k))) as string[],
      defaults,
    );
    return {
      ...s,
      mode,
      list: built.list,
      exclusions: built.exclusions,
      exclusionsKnown: true,
      touched: withTouched(s, built.list),
    };
  }
  const exclusions = s.exclusionsKnown
    ? normaliseExclusions(s.exclusions, s.list)
    : normaliseExclusions(keys, s.list);
  return { ...s, mode, exclusions, exclusionsKnown: true };
}

function setAllFields(s: FieldScopeState, on: boolean): FieldScopeState {
  if (on) {
    if (s.list === null) return s;
    return {
      ...s,
      list: null,
      beforeAllFields: {
        list: s.list,
        mode: s.mode,
        exclusions: s.exclusions,
        exclusionsKnown: !!s.exclusionsKnown,
      },
    };
  }
  if (s.list !== null) return s;
  const prior = s.beforeAllFields;
  if (prior) {
    return {
      ...s,
      list: [...prior.list],
      mode: prior.mode,
      exclusions: [...prior.exclusions],
      exclusionsKnown: prior.exclusionsKnown,
      beforeAllFields: null,
    };
  }
  return { ...s, list: [], mode: "include", beforeAllFields: null };
}

/** The one reducer every field-scope writer goes through. */
export function fieldScopeReducer(s: FieldScopeState, a: FieldScopeAction): FieldScopeState {
  switch (a.type) {
    case "setAllFields":
      return setAllFields(s, a.on);
    case "setMode":
      return setMode(s, a.mode, a.catalogue);
    case "show":
    case "addList":
      return showKeys(s, a.keys);
    case "hide":
      return hideKeys(s, a.keys);
    case "replaceList":
      return replaceList(s, a.list);
    default:
      return s;
  }
}

/**
 * The editor's view. Visible = every list key (a projection token riding with
 * its column is no row of its own); Hidden = the rest of the catalogue minus
 * what the list reveals. `catalogue === null` (loading / failed): no orphan or
 * outside-catalogue marking, Hidden lists the exclusions, and no tag that
 * needs the catalogue — a custom field is not called "sensitive" on a guess
 * (B2 F1 U-LOW-2); the access data and the reveal rule need none and stay.
 * (`needsSensitiveConfirm` still treats an unknown level as sensitive — and
 * the pane is read-only meanwhile.) REVEALS, tags "new" and `revealedVia`
 * apply in "All except" only.
 */
export function groupForEditor(s: FieldScopeState, catalogue: readonly CatalogueEntry[] | null): EditorGroups {
  const idx = catalogue ? catalogueIndex(catalogue) : null;
  if (s.list === null) {
    const all = idx ? [...idx.keys()] : [];
    return {
      visible: all.map((key) => ({ key })),
      hidden: [],
      revealedVia: [],
      newNormal: [],
      counts: idx ? { visible: idx.size, total: idx.size } : null,
    };
  }
  const exclude = s.mode === "exclude";
  const listKeys = dedupe(s.list);
  const onList = new Set(listKeys);
  const excluded = new Set(s.exclusions);
  const revealed = exclude ? revealedBy(listKeys) : new Set<string>();
  const readable = (k: string) => onList.has(k) || (revealed.has(k) && !excluded.has(k));

  const visible: VisibleRow[] = [];
  for (const key of listKeys) {
    if (ridesWithColumn(key, onList)) continue;
    const brokenFor = exclude ? revealsOf(key).filter((k) => excluded.has(k) && !onList.has(k)) : [];
    if (brokenFor.length > 0) visible.push({ key, marker: "revealsLastName", reveals: brokenFor });
    // A legacy dotted key is "deleted" only when its BASE field is missing
    // from a successful read (contract §7); else it is a normal row.
    else if (idx && isCustomKey(key) && !idx.has(customBaseKey(key))) visible.push({ key, marker: "deleted" });
    else if (isImpliedAllowlistToken(key)) visible.push({ key, marker: "stray" });
    else if (idx && !isCustomKey(key) && !idx.has(key)) visible.push({ key, marker: "outsideCatalogue" });
    else visible.push({ key });
  }

  const sensitive = (key: string): boolean => {
    if (ACCESS_DATA_SET.has(key)) return true;
    if (!idx) return false; // unknown: no tag on a guess (U-LOW-2)
    const e = idx.get(key);
    return e ? isRestricted(e.sensitivity) : isCustomKey(key);
  };
  // A legacy dotted key is never offered in Hidden (contract §7).
  const pool = (idx ? [...idx.keys()] : dedupe(s.exclusions)).filter((k) => !isDottedCustomKey(k));
  const hidden: HiddenRow[] = [];
  for (const key of pool) {
    if (onList.has(key) || revealed.has(key) || isImpliedAllowlistToken(key)) continue;
    const withheldFor = exclude ? revealsOf(key).filter((k) => !readable(k)) : [];
    if (withheldFor.length > 0) hidden.push({ key, tag: "revealsLastName", reveals: withheldFor });
    else if (sensitive(key)) hidden.push({ key, tag: "sensitive" });
    else if (exclude && !excluded.has(key)) hidden.push({ key, tag: "new" });
    else hidden.push({ key });
  }

  const revealedVia: { key: string; via: string[] }[] = [];
  for (const key of revealed) {
    if (onList.has(key)) continue;
    const via = listKeys.filter((r) => !ridesWithColumn(r, onList) && revealsOf(r).includes(key));
    revealedVia.push({ key, via });
  }

  let counts: EditorGroups["counts"] = null;
  if (idx) {
    let n = 0;
    for (const key of idx.keys()) if (onList.has(key) || revealed.has(key)) n++;
    counts = { visible: n, total: idx.size };
  }

  return {
    visible,
    hidden,
    revealedVia,
    newNormal: hidden.filter((h) => h.tag === "new").map((h) => h.key),
    counts,
  };
}

/**
 * The body fields a save sends. "All fields" ⇒ `{fieldAllowlist: null}` and
 * nothing else; "Only these" ⇒ mode "include" (the backend unsets the
 * metadata); "All except" ⇒ mode "exclude" + normalised exclusions (never
 * null). The list goes through its mode's canonicaliser.
 */
export function payloadOf(s: FieldScopeState): FieldScopePayload {
  if (s.list === null) return { fieldAllowlist: null };
  if (s.mode === "exclude") {
    const list = keepStrayCanonical(s.list) as string[];
    return {
      fieldAllowlist: list,
      fieldAllowlistMode: "exclude",
      fieldExclusions: normaliseExclusions(s.exclusions, list),
    };
  }
  return { fieldAllowlist: normalizeAllowlist([...s.list]), fieldAllowlistMode: "include" };
}

/**
 * The Roles list badge "N fields not released": "All except" rows only —
 * catalogue keys neither on the list nor excluded nor revealed by the list,
 * SMS built-ins left out while the channel is off. 0 for every other row.
 */
export function notReleasedCount(
  t: FieldScopeSource,
  catalogue: readonly CatalogueEntry[],
  opts: { smsEnabled: boolean },
): number {
  if (t.fieldAllowlistMode !== "exclude" || !Array.isArray(t.fieldAllowlist)) return 0;
  const onList = new Set(t.fieldAllowlist);
  const excluded = new Set(Array.isArray(t.fieldExclusions) ? t.fieldExclusions : []);
  const revealed = revealedBy(t.fieldAllowlist);
  let n = 0;
  for (const key of catalogueIndex(withoutSmsEntriesUnless(catalogue, opts.smsEnabled)).keys()) {
    if (!onList.has(key) && !excluded.has(key) && !revealed.has(key)) n++;
  }
  return n;
}

/**
 * Over the backend's 200-key cap? Measured on the list the save sends: the
 * default ("Only these") canonicaliser is `normalizeAllowlist`; "All except"
 * keeps stray tokens and is measured with them.
 */
export function exceedsCap(list: readonly string[] | null, mode: AllowlistMode = "include"): boolean {
  if (list === null) return false;
  return (canonicalFor(mode, list) as string[]).length > FIELD_ALLOWLIST_CAP;
}
